import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Microphone, MicrophoneSlash, Sparkle, SpeakerHigh, X, CircleNotch, PaperPlaneRight, ArrowCounterClockwise, Brain } from "@phosphor-icons/react";
import { useVoiceLoop } from "@/hooks/useVoiceLoop";
import { ClosingCard } from "@/components/assistant/ClosingCard";
import { AssistantMemory } from "@/components/assistant/AssistantMemory";

const LABEL = { listening: "Sun rahi hoon…", thinking: "Dekh rahi hoon…", speaking: "Bol rahi hoon…" };

const ttsUrl = async (text) => {
  const { data } = await api.post("/assistant/tts", { text: text.slice(0, 1400) }, { responseType: "blob", timeout: 60000 });
  return URL.createObjectURL(data);
};

export const HeyShobhit = ({ onNavigate }) => {
  const [q, setQ] = useState("");
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [memOpen, setMemOpen] = useState(false);
  const voiceRef = useRef(null);
  const endRef = useRef(null);
  const boxRef = useRef(null);

  const ask = useCallback(async (text, fromVoice) => {
    const t = (text || "").trim();
    if (!t) { if (fromVoice) voiceRef.current?.resume(); return; }
    setBusy(true); setQ(""); setShow(true);
    try {
      const { data } = await api.post("/assistant/ask", { text: t }, { timeout: 90000 });
      setLog((l) => [...l.slice(-19), { q: t, answer: data.answer, cards: data.cards }]);
      setBusy(false);
      try { voiceRef.current?.speak(await ttsUrl(data.answer)); } catch (_e) { voiceRef.current?.resume(); }
    } catch (e) {
      setBusy(false);
      toast.error(formatApiError(e.response?.data?.detail));
      if (fromVoice) voiceRef.current?.resume();
    }
  }, []);

  const onUtterance = useCallback(async (blob, mime) => {
    if (blob.size < 1500) return voiceRef.current?.resume();
    try {
      const fd = new FormData();
      fd.append("file", blob, mime.includes("mp4") ? "voice.mp4" : "voice.webm");
      const { data } = await api.post("/assistant/stt", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      ask(data.text, true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); voiceRef.current?.resume(); }
  }, [ask]);

  const voice = useVoiceLoop({ onUtterance });
  voiceRef.current = voice;

  useEffect(() => { api.get("/assistant/history").then(({ data }) => setLog(data.messages || [])).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [log, busy]);
  useEffect(() => {
    const h = (e) => { if (memOpen || voiceRef.current?.active) return; if (boxRef.current && !boxRef.current.contains(e.target)) setShow(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [memOpen]);
  const go = (page) => { setShow(false); onNavigate?.(page); };

  const toggleMic = async () => {
    if (voice.active) return voice.stop();
    setShow(true);
    if (!(await voice.start())) toast.error("Mic ki permission do");
  };
  const fresh = async () => { await api.post("/assistant/new").catch(() => {}); setLog([]); voice.stopAudio(); toast.success("Nayi baat shuru"); };
  const status = voice.active ? LABEL[voice.mode] : busy ? LABEL.thinking : "";

  return (
    <div ref={boxRef} className="relative w-full" data-testid="hey-shobhit">
      <form onSubmit={(e) => { e.preventDefault(); ask(q); }} className="flex items-center gap-1.5">
        <button type="button" onClick={toggleMic} title={voice.active ? "Band karo" : "Baat karo"} data-testid="hey-shobhit-mic"
          className={"h-10 w-10 shrink-0 rounded-sm flex items-center justify-center transition-colors " + (voice.active ? "bg-red-600 text-white animate-pulse" : "bg-orange-500 hover:bg-orange-600 text-white")}>
          {voice.active ? <MicrophoneSlash size={18} weight="fill" /> : <Microphone size={18} weight="fill" />}
        </button>
        <div className="relative flex-1">
          <Sparkle size={16} weight="fill" className="absolute left-3 top-1/2 -translate-y-1/2 text-orange-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => log.length && setShow(true)} placeholder="Hey Shobhit"
            className="pl-9 h-10 rounded-sm bg-slate-950 border-slate-700" data-testid="hey-shobhit-input" />
        </div>
        <button type="submit" disabled={busy || !q.trim()} data-testid="hey-shobhit-send"
          className="h-10 w-10 shrink-0 rounded-sm border border-slate-700 bg-slate-950 text-slate-300 flex items-center justify-center disabled:opacity-40"><PaperPlaneRight size={16} /></button>
      </form>
      {status && <div className={"text-[11px] mt-1 " + (voice.mode === "listening" ? "text-red-300" : "text-orange-300")} data-testid="hey-shobhit-status">{status}</div>}
      {show && (log.length > 0 || busy) && (
        <div className="absolute left-0 right-0 top-12 z-40 rounded-sm border border-slate-700 bg-slate-950 shadow-2xl max-h-[70vh] overflow-y-auto min-w-[300px]" data-testid="hey-shobhit-panel">
          <div className="sticky top-0 bg-slate-950 border-b border-slate-800 px-3 py-1.5 flex items-center gap-3 text-[11px]">
            <button onClick={fresh} className="text-slate-300 hover:text-white flex items-center gap-1" data-testid="hey-shobhit-new"><ArrowCounterClockwise size={12} /> Nayi baat</button>
            <button onClick={() => setMemOpen(true)} className="text-slate-300 hover:text-white flex items-center gap-1" data-testid="hey-shobhit-memory"><Brain size={12} /> Memory</button>
            <button onClick={() => { setShow(false); voice.stopAudio(); }} className="ml-auto text-slate-500 hover:text-slate-200" data-testid="hey-shobhit-close"><X size={14} /></button>
          </div>
          <div className="p-3 space-y-3">
            {log.map((m, i) => (
              <div key={i} className="text-sm space-y-1.5">
                <div className="text-slate-400">{m.q}</div>
                <div className="text-slate-100 flex gap-2" data-testid={`hey-shobhit-answer-${i}`}>
                  <span className="flex-1 whitespace-pre-wrap">{m.answer}</span>
                  <button onClick={async () => { try { voice.speak(await ttsUrl(m.answer)); } catch (_e) { /* ignore */ } }} className="text-orange-400 shrink-0" data-testid={`hey-shobhit-speak-${i}`}><SpeakerHigh size={16} /></button>
                </div>
                {(m.cards || []).map((c, j) => <ClosingCard key={j} c={c} onNavigate={onNavigate ? go : null} />)}
              </div>
            ))}
            {busy && <div className="text-xs text-slate-300 flex items-center gap-2" data-testid="hey-shobhit-busy"><CircleNotch className="animate-spin" /> {LABEL.thinking}</div>}
            <div ref={endRef} />
          </div>
        </div>
      )}
      <Dialog open={memOpen} onOpenChange={setMemOpen}>
        <DialogContent className="max-w-2xl w-[96vw] max-h-[90vh] overflow-y-auto rounded-sm bg-slate-950 border-slate-800">
          <DialogHeader><DialogTitle className="sr-only">Memory</DialogTitle></DialogHeader>
          <AssistantMemory />
        </DialogContent>
      </Dialog>
    </div>
  );
};
